import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppOneDrive - the page's OneDrive rules (wave 3 of documentation/design/storage-providers.md). Habitat-free: no
// `window`, no `document` - the Bridge suite imports it (`AppCsp.test.ts` reads `ConnectOrigins`). The browser half
// (sessionStorage, the upload, the per-file reads) is `AppOneDriveRun.ts`.
//
// ⛔ DORMANT: everything here is reached only when the service OFFERS OneDrive - `/onedrive` exists only while the
// service's `WAC_ONEDRIVE` is "1", and a `/folder` / `/storage` answer says `storage: "onedrive"` only then.
//
// ⭐ THE RETURN. Microsoft sends the subscriber back to https://wa.kalmstrom.com/?code=..&state=.. - a NEW page load.
// Before leaving, the page keeps {state, number, proof} in `sessionStorage` (this tab, this origin only); on the way
// back `ReturnIn` accepts the code ONLY when the address's state equals the kept one (a forged return - somebody else's
// code planted in a link - is refused), and the record is deleted the moment it is read. ⛔ That is the one place the
// sign-up secret is written down at all: tab-scoped, <= `PendingMs` old, gone on return (the alternative - a key in
// `localStorage` - outlives the tab; the secret alone cannot be re-asked after a reload).
// ⛔ ONLY ON THE REGISTERED ORIGIN: the redirect is fixed, so a page on any other origin could never read its own record
// back - it is not offered there (`OriginOk`).

/** What the page keeps across Microsoft's sign-in page. */
export class AppOneDrivePending {
    public State: string = "";
    public Number: string = "";
    public Secret: string = "";
    public Key: string = "";
    public AtMs: number = 0;
}

/** What a return address says: a code to redeem, a sentence, or nothing (not a OneDrive return at all). */
export class AppOneDriveReturn {
    /** True when the address carried `code`, `state` or `error` - it must be stripped from the address either way. */
    public Seen: boolean = false;
    public Code: string = "";
    public Why: string = "";
}

export class AppOneDriveRules {
    public static readonly Route: string = "/onedrive";
    /** The Entra app's one registered redirect (`OneDriveAuth.RedirectUri`, pinned by `AppOneDrive.test.ts`). */
    public static readonly Origin: string = "https://wa.kalmstrom.com";
    public static readonly StorageKey: string = "wac.onedrive.pending";
    public static readonly PendingMs: number = 30 * 60 * 1000;
    /** Graph insists every chunk but the last is a multiple of 320 KiB. */
    public static readonly ChunkBytes: number = 10 * 320 * 1024;
    /**
     * The ONE host the BROWSER talks to for a OneDrive archive: personal OneDrive's pre-authenticated upload address
     * (UNMEASURED - storage-providers.md). ⛔ No wildcard (`AppCsp.test.ts`): reads come through the service, and a work
     * account's upload host (`<tenant>.sharepoint.com`) is refused by the service with a sentence.
     */
    public static readonly ConnectOrigins: string[] = ["https://api.onedrive.com"];

    public static readonly ForgedWhy: string = "That OneDrive answer did not come from this sign-up, so nothing was"
        + " connected. Start the OneDrive step again.";
    public static readonly LostWhy: string = "This page lost track of the OneDrive step (it took longer than half an hour,"
        + " or another tab was used). Start the OneDrive step again.";
    public static readonly DeclinedWhy: string = "OneDrive was not connected - Microsoft said it was not allowed. You can"
        + " try again, or use Dropbox instead.";

    /** DOCUMENTED EXCEPTION: compares two strings. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static OriginOk = (Origin: string): boolean => {
        return nz(Origin) === AppOneDriveRules.Origin;
    };

    /** A fresh nonce from 16 random bytes (hex). `Bytes` is filled by the caller's crypto - kept pure for the tests. */
    public static StateOf = (Bytes: Uint8Array): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = "";
            for (let At: number = 0; At < Bytes.length; At = At + 1) { Out = Out + (Bytes[At] + 256).toString(16).substring(1); }
            if (Out.length < 32) { return ""; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveRules.StateOf");
            return "";
        }
    };

    /** GET /onedrive?state=.. ("" when there is no service or no state). */
    public static UrlFor = (ServiceUrl: string, State: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(ServiceUrl).length === 0 || nz(State).length === 0) { return ""; }
            return nz(ServiceUrl).replace(/\/+$/, "") + AppOneDriveRules.Route + "?state=" + encodeURIComponent(State);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveRules.UrlFor");
            return "";
        }
    };

    public static PendingText = (One: AppOneDrivePending): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return JSON.stringify({ state: One.State, number: One.Number, secret: One.Secret, key: One.Key, at: One.AtMs });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveRules.PendingText");
            return "";
        }
    };

    /** The kept record, or null (absent, unreadable, or older than `PendingMs`). */
    public static PendingIn = (Text: string, NowMs: number): AppOneDrivePending | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Raw: Record<string, unknown> = JSON.parse(nz(Text)) as Record<string, unknown>;
            if (Raw === null || typeof Raw !== "object") { return null; }
            if (typeof Raw.state !== "string" || typeof Raw.number !== "string" || typeof Raw.at !== "number") { return null; }
            const Out: AppOneDrivePending = new AppOneDrivePending();
            Out.State = Raw.state as string; Out.Number = Raw.number as string; Out.AtMs = Raw.at as number;
            if (typeof Raw.secret === "string") { Out.Secret = Raw.secret as string; }
            if (typeof Raw.key === "string") { Out.Key = Raw.key as string; }
            if (NowMs - Out.AtMs > AppOneDriveRules.PendingMs || NowMs < Out.AtMs) { return null; }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable record (another build, a hand edit) is "no record" - the
            // subscriber is told to start the step again (`LostWhy`), never an error report.
            return null;
        }
    };

    /**
     * The return address (`location.search`) against the kept record.
     * ⛔ THE STATE MUST MATCH, exactly - else `ForgedWhy` and NO code. No record = `LostWhy`. `error=` = `DeclinedWhy`.
     */
    public static ReturnIn = (Search: string, Kept: AppOneDrivePending | null): AppOneDriveReturn => {
        const Out: AppOneDriveReturn = new AppOneDriveReturn();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            const Params: URLSearchParams = new URLSearchParams(nz(Search));
            const Code: string = nz(Params.get("code"));
            const State: string = nz(Params.get("state"));
            const Err: string = nz(Params.get("error"));
            Out.Seen = Code.length > 0 || State.length > 0 || Err.length > 0;
            if (Out.Seen === false) { return Out; }
            if (Err.length > 0) { Out.Why = AppOneDriveRules.DeclinedWhy; return Out; }
            if (Kept === null) { Out.Why = AppOneDriveRules.LostWhy; return Out; }
            if (State.length === 0 || State !== Kept.State || Code.length === 0) { Out.Why = AppOneDriveRules.ForgedWhy; return Out; }
            Out.Code = Code;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveRules.ReturnIn");
            return Out;
        }
    };

    /** The grant body: the code plus the SAME proof `/dropbox` takes (the secret, else the key). "" = nothing to send. */
    public static GrantBody = (Kept: AppOneDrivePending, Code: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Code).length === 0 || nz(Kept.Number).length === 0) { return ""; }
            const Out: Record<string, unknown> = { number: Kept.Number, code: Code };
            if (nz(Kept.Secret).length > 0) { Out.secret = Kept.Secret; } else if (nz(Kept.Key).length > 0) { Out.key = Kept.Key; } else { return ""; }
            return JSON.stringify(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveRules.GrantBody");
            return "";
        }
    };

    /** One file's address request: `{number, key, action: "file", path}`. "" without a key. */
    public static FileBody = (Number: string, Key: string, Path: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Number).length === 0 || nz(Key).length === 0 || nz(Path).length === 0) { return ""; }
            return JSON.stringify({ number: Number, key: Key, action: "file", path: Path });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveRules.FileBody");
            return "";
        }
    };

    /** The `Content-Range` of the chunk starting at `Sent` of `Total` bytes. */
    public static RangeOf = (Sent: number, Total: number): string => {
        const End: number = Math.min(Sent + AppOneDriveRules.ChunkBytes, Total);
        return "bytes " + String(Sent) + "-" + String(End - 1) + "/" + String(Total);
    };
}
